/**
 * Adjustment Lock Utilities
 * 
 * Shared utility functions for determining adjustment lock state
 * and button rendering mode across all dynamic working paper components.
 * 
 * Backend now exclusively uses `adjustments: AdjustmentDetail[]` array.
 * The old `adjustment` (single value) and `adjustHeaderId` fields have been removed.
 */

import { AdjustmentMode } from '@/services/auditService/adjustmentModes';
import type { AdjustmentDetail } from '@/services/auditService/interface/wp-row';

/**
 * Determines if a row's adjustment field should be locked (read-only but clickable).
 * A row is locked if it has a non-empty `adjustments` array (i.e. backend has adjustment data).
 * 
 * @param row - Any row object with an adjustments field
 * @returns true if the adjustment field should be locked
 */
export function isAdjustmentLocked(row: any): boolean {
    return Array.isArray(row?.adjustments) && row.adjustments.length > 0;
}

/**
 * Button rendering mode for dynamic components.
 * - 'save': Only locked (backend) adjustments exist, or no adjustments at all → show Save button
 * - 'adjustment': User has added/modified a non-locked adjustment → show Adjustment button
 */
export type ButtonMode = 'save' | 'adjustment';

/**
 * Determines whether a dynamic component should render a Save or Adjustment button.
 * 
 * Rules:
 * - If any row has a non-zero adjustment total that is NOT locked → 'adjustment'
 * - If all non-zero adjustments are locked (from backend) or no adjustments → 'save'
 * 
 * @param rows - Array of row objects (CommonRow, DueFromToDirRow, etc.)
 * @param pendingAdjustments - Optional pending adjustments being added via AdjustmentCell
 * @returns 'save' or 'adjustment'
 */
export function determineButtonMode(rows: any[], pendingAdjustments?: Record<number, number>): ButtonMode {
    if (!rows || rows.length === 0) return 'save';

    // Check if any row has pending adjustments (new adjustments being added)
    if (pendingAdjustments && Object.keys(pendingAdjustments).length > 0) {
        const hasPendingValue = Object.values(pendingAdjustments).some(val => Math.abs(val) > 0.01);
        if (hasPendingValue) return 'adjustment';
    }

    const hasNewUserAdjustment = rows.some(row => {
        const adjTotal = getAdjustmentTotal(row);
        return Math.abs(adjTotal) > 0.01 && !isAdjustmentLocked(row);
    });

    return hasNewUserAdjustment ? 'adjustment' : 'save';
}

/**
 * Checks if any row in the array has backend adjustment data (non-empty adjustments array).
 * 
 * @param rows - Array of row objects
 * @returns true if any row has a non-empty adjustments array
 */
export function hasAnyBackendAdjustment(rows: any[]): boolean {
    if (!rows || rows.length === 0) return false;
    return rows.some(row => Array.isArray(row?.adjustments) && row.adjustments.length > 0);
}

/**
 * Computes the total adjustment value for a row from its `adjustments` array.
 * Returns 0 if no adjustments exist.
 * 
 * @param row - Any row object with an adjustments field
 * @returns The total adjustment amount
 */
export function getAdjustmentTotal(row: any): number {
    if (Array.isArray(row?.adjustments) && row.adjustments.length > 0) {
        return row.adjustments.reduce((sum: number, d: any) => sum + (Number(d?.adjustment) || 0), 0);
    }
    // Fallback to manual single adjustment field (user input when no backend adjustments)
    return Number(row?.adjustment) || 0;
}

/**
 * Computes only the backend-saved adjustment total (from `adjustments` array).
 * Never includes the singular `adjustment` field (user-entered new value).
 * Used for WpPerCheck balance validation where new pending adjustments should NOT affect the check.
 */
export function getBackendAdjustmentTotal(row: any): number {
    if (Array.isArray(row?.adjustments) && row.adjustments.length > 0) {
        return row.adjustments.reduce((sum: number, d: any) => sum + (Number(d?.adjustment) || 0), 0);
    }
    return 0;
}

/**
 * Sum NEW user adjustments for fromPaper / WP→WP `amountDifference`.
 * Excludes backend-saved `adjustments[]` (locked rows); includes singular `adjustment` + pending only.
 * WHY: using getAdjustmentTotal / totals.adjustment folds already-persisted adj into fromPaper.
 */
export function sumNewAdjustmentDifference(
    rows: any[] | null | undefined,
    pendingAdjustments: Record<number, number> = {},
): number {
    if (!rows || rows.length === 0) return 0;
    return rows.reduce((sum, row) => {
        const pending = row?.id != null ? (Number(pendingAdjustments[row.id]) || 0) : 0;
        const newAdj = isAdjustmentLocked(row) ? 0 : (Number(row?.adjustment) || 0);
        return sum + newAdj + pending;
    }, 0);
}

/**
 * Checks if a row has multiple adjustment entries.
 * 
 * @param row - Any row object
 * @returns true if the row has more than one adjustment detail
 */
export function hasMultipleAdjustments(row: any): boolean {
    return Array.isArray(row?.adjustments) && row.adjustments.length > 1;
}

/**
 * 获取有 adjustment 值的行的 item 名称列表（用于 Save 确认弹窗消息）。
 * 
 * @param rows - 行数组
 * @param pendingAdjustments - 可选 pending adjustments
 * @param itemField - item 字段名，默认 'item'
 * @returns 有 adjustment 值的行的 item 名称数组
 */
export function getAdjustmentItemNames(
    rows: any[],
    pendingAdjustments?: Record<number, number>,
    itemField: string = 'item',
): string[] {
    if (!rows || rows.length === 0) return [];
    const names: string[] = [];
    rows.forEach(row => {
        // Only include rows with NEW (non-locked) adjustments, not backend-saved ones
        const hasNewAdj = !isAdjustmentLocked(row) && Math.abs(Number(row?.adjustment) || 0) > 0.01;
        const hasPending = pendingAdjustments && row?.id != null
            && Math.abs(pendingAdjustments[row.id] || 0) > 0.01;
        if (hasNewAdj || hasPending) {
            const name = String((row as any)?.[itemField] || '').trim();
            if (name) names.push(name);
        }
    });
    return names;
}

/**
 * Checks if a row has any adjustment entries.
 * 
 * @param row - Any row object
 * @returns true if the row has at least one adjustment detail
 */
export function hasAdjustments(row: any): boolean {
    return Array.isArray(row?.adjustments) && row.adjustments.length > 0;
}

/** 判断单条 adjustment 是否为系统自动生成（ADJUST_AUTO_GEN） */
export function isAutoAdjustment(a: AdjustmentDetail): boolean {
    return a.adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;
}

/** 只计算手动调整的合计（排除 ADJUST_AUTO_GEN） */
export function getManualAdjustmentTotal(row: any): number {
    if (Array.isArray(row?.adjustments) && row.adjustments.length > 0) {
        return row.adjustments
            .filter((a: AdjustmentDetail) => !isAutoAdjustment(a))
            .reduce((sum: number, a: AdjustmentDetail) => sum + (Number(a?.adjustment) || 0), 0);
    }
    return Number(row?.adjustment) || 0;
}

/** 从 row.autoAdj 或 adjustments 中提取 auto adjustment（ADJUST_AUTO_GEN）合计 */
export function getAutoAdjustmentFromRow(row: any): number {
    // 优先使用后端直接设置的 autoAdj 字段
    if (row?.autoAdj != null && Math.abs(Number(row.autoAdj)) > 0.001) {
        return Number(row.autoAdj) || 0;
    }
    if (!Array.isArray(row?.adjustments) || row.adjustments.length === 0) return 0;
    return row.adjustments
        .filter((a: AdjustmentDetail) => isAutoAdjustment(a))
        .reduce((sum: number, a: AdjustmentDetail) => sum + (Number(a?.adjustment) || 0), 0);
}

/** FS 节点 adjustment 是否为系统自动生成 */
export function isFsAutoAdjustmentEntry(adj: any): boolean {
    return adj?.isAutoAdj === true || adj?.adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;
}

/** 父级 FS 节点 Per 值（opening + 手动 adjustment，不含 AutoAdjust） */
export function getManualAuditedFromFsLine(
    line: { openingAmount?: number; amount?: number; adjustment?: any[] } | null | undefined,
    perData: { clientValue?: number } | null | undefined,
): number {
    const opening = Number(perData?.clientValue ?? line?.openingAmount ?? line?.amount ?? 0) || 0;
    if (!Array.isArray(line?.adjustment) || line.adjustment.length === 0) {
        return opening;
    }
    const manualAdj = line.adjustment
        .filter((a) => !isFsAutoAdjustmentEntry(a))
        .reduce((sum, a) => sum + (Number(a?.adjustment) || 0), 0);
    return opening + manualAdj;
}

/** 表格 Manual Final 合计：perClient + manualAdj + pendingAdj */
export function sumTableManualFinalTotal(
    rows: any[] | null | undefined,
    pendingAdjustments: Record<number, number> = {},
): number {
    return (rows || []).reduce((sum, row) => {
        const pending = row?.id != null ? (pendingAdjustments[row.id] || 0) : 0;
        return sum + (Number(row?.perClient) || 0) + getManualAdjustmentTotal(row) + pending;
    }, 0);
}

/** 表格 PerCheck 合计：perClient + 已保存手动 adjustment（不含 pending / 未保存输入） */
export function sumTablePerCheckTotal(rows: any[] | null | undefined): number {
    return (rows || []).reduce((sum, row) => {
        const pc = Number(row?.perClient) || 0;
        if (Array.isArray(row?.adjustments) && row.adjustments.length > 0) {
            return sum + pc + getManualAdjustmentTotal(row);
        }
        return sum + pc;
    }, 0);
}
